/*
  Analytics SDK — privacy-first, session-scoped, fire-and-forget.

  Usage:
    import { track } from "@/lib/analytics";
    track("click", { cta: "home-cta-primary" });

  - Generates an anonymous session_id (sessionStorage) — cleared when the
    tab closes.
  - Reads UTM params from the current URL on every fire.
  - Posts to /api/analytics/event. Network failures are swallowed.
  - Never stores or sends free-text content.
*/
import { api } from "./api";

const SESSION_KEY = "ss_analytics_session";

function genId() {
  try {
    return (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) { s = genId(); sessionStorage.setItem(SESSION_KEY, s); }
    return s;
  } catch { return genId(); }
}

function readUtm() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ["source", "medium", "campaign", "content", "term"].forEach((k) => {
      const v = params.get(`utm_${k}`);
      if (v) utm[k] = v;
    });
    return Object.keys(utm).length ? utm : null;
  } catch { return null; }
}

let _enabled = true;
export function setAnalyticsEnabled(v) { _enabled = !!v; }

export async function track(event, properties = {}) {
  if (!_enabled) return;
  if (typeof event !== "string" || !event) return;
  const payload = {
    event,
    session_id: getSessionId(),
    path: (typeof window !== "undefined" ? window.location.pathname : null),
    referrer: (typeof document !== "undefined" ? document.referrer : null) || null,
    utm: readUtm(),
    properties: properties || {},
  };
  try { await api.post("/analytics/event", payload); } catch {}
}

// Convenience wrapper for click tracking.
export function trackClick(cta, extra = {}) {
  return track("click", { cta, ...extra });
}
